import type { BranchKey } from '@festina-life/api'
import {
  assertNotEmpty,
  type SetRequiredAndNonNullable,
} from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles } from '@festina-life/ui/libs'
// oxlint-disable-next-line no-restricted-imports - DisclosureSection is allowed in the config portal
import { DisclosureSection } from '@festina-life/ui/listsAndTables'
import cx from 'classnames'

import { useChangesetStateContext } from '../../../../context/changesetStateContext'
import { useNavigationParams } from '../../../../system/useNavigationParams'
import type { ConciseChangesetWithBranchKey } from '../../../../uiModels/changesets/types'
import { ChangesetAttributes } from './ChangesetAttributes'
import { ChangesetAuthor } from './ChangesetAuthor'
import { ConciseChangesetDetails } from './ConciseChangesetDetails'
import { PullChangesetButton } from './PullChangesetButton'
import type { ChangesetOrigin } from './types'

type Props = {
  changeset: SetRequiredAndNonNullable<
    ConciseChangesetWithBranchKey,
    'changesetKey'
  >
  branchKey: BranchKey
  isForceExpanded?: boolean
  origin: ChangesetOrigin
  isPullable?: boolean
}

export const ConciseChangesetDisclosure = ({
  changeset,
  branchKey,
  isForceExpanded,
  origin,
  isPullable,
}: Props) => {
  const translate = useTranslate()
  const { disabledChangesetKeys, onEnable, onDisable } =
    useChangesetStateContext()
  const { branch: currentBranch, server } = useNavigationParams()
  const isCurrentBranch =
    currentBranch &&
    branchKey &&
    currentBranch === branchKey.name &&
    server === branchKey.server

  const isDisabled = disabledChangesetKeys.includes(changeset.changesetKey)

  const handleToggleChangesetState = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault()
    event.stopPropagation()

    assertNotEmpty(changeset.changesetKey)

    if (isDisabled) {
      onEnable([changeset.changesetKey])
    } else {
      onDisable([changeset.changesetKey])
    }
  }

  return (
    <div className="flex items-center gap-x-2 w-full overflow-x-hidden">
      {isCurrentBranch && (
        <IconButton
          tip={translate('action.toggleVisibility')}
          iconName={isDisabled ? 'EyeSlash' : 'Eye'}
          onClick={handleToggleChangesetState}
          isDanger={isDisabled}
          iconSize="sm"
          className="!-p0"
        />
      )}
      <DisclosureSection
        className="min-w-0 flex-1"
        isOpenByDefault={isForceExpanded}
      >
        <DisclosureSection.Title
          className={cx(
            'flex gap-x-2 items-center w-full rounded-none! text-sm py-0!',
            paperStyles({ isFaded: true, borders: ['all'] }),
          )}
        >
          <ChangesetAuthor changeset={changeset} />
          <div
            className={cx(
              'min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left',
              changeset.description ? '' : 'opacity-40',
            )}
          >
            {changeset.description ?? translate('changesetList.noDescription')}
          </div>
          <div className="shrink-0" />
          <ChangesetAttributes
            changeset={changeset}
            className="shrink-0 hidden @md:flex"
          />
        </DisclosureSection.Title>

        <DisclosureSection.Body className="pl-2! w-full min-w-0 flex flex-col pt-2">
          <ConciseChangesetDetails
            branchKey={branchKey}
            changeset={changeset}
            origin={origin}
            pullButton={
              isPullable ? (
                <PullChangesetButton
                  branchKey={branchKey}
                  changeset={changeset}
                />
              ) : undefined
            }
          />
        </DisclosureSection.Body>
      </DisclosureSection>
    </div>
  )
}
